import React, { useMemo, useState } from 'react';
import SEO from '../components/SEO';
import { FIRMA } from '../data/firma';
import styles from '../css/modules/TestSensomotoryczny.module.css';

const QUESTIONS = Object.freeze([
  'Czy dziecko ma problem z koncentracją w głośnym otoczeniu?',
  'Czy dziecko zatyka uszy przy hałasie (odkurzacz, suszarka itp)?',
  'Czy dziecku przeszkadzają metki w ubraniach, ciasne ubrania?',
  'Czy dziecko często wpada na meble, potyka się, ma trudność z utrzymaniem równowagi?',
  'Czy dziecku ma problem z jedzeniem posiłków ze względu na kolor, konsystencję, zapach?',
  'Czy dziecko reaguje nadmiernie na zadrapanie/uderzenie/lekki ból (krzyk, płacz itp)?',
  'Czy dziecko lubi mocne ściskanie, przytulanie, przeciskanie się przez przedmioty np. meble?',
  'Czy dziecko nadmiernie poszukuje ruchu (kręcenie, huśtanie, skakanie, wspinanie itd) nawet podczas odpoczynku?',
  'Czy dziecko ma problem podczas sekwencji ruchów (zabawy ruchowe, sport, taniec)?',
  'Czy dziecko z trudnością manipuluje małymi przedmiotami np. przy zapinaniu guzików, układaniu puzzli itp?',
  'Czy dziecko reaguje nadmiernym lękiem podczas wspinania, jazdy na rowerze, huśtania, zjeżdżania na zjeżdżalni?',
  'Czy dziecko często wkłada przedmioty do buzi (ołówki, rękawy, zabawki)?',
  'Czy dziecko bywa nadmiernie pobudzone po powrocie z przedszkola, szkoły, galerii handlowej itp?',
  'Czy dziecko nie lubi zabaw różnymi fakturami (plastelina, suchy/mokry piasek, masy plastyczne, klej)?',
  'Czy dziecko niechętnie myje twarz i głowę podczas kąpieli?',
  'Czy dziecko ma trudność ze spokojnym siedzeniem podczas lekcji, posiłków, odpoczynku?',
]);

function getFeedback(pct) {
  if (pct >= 70) {
    return (
      <>
        <p><strong>70-100%</strong> - wstępny test sensomotoryczny pokazuje, że warto bliżej przyjrzeć się funkcjonowaniu Waszego dziecka.</p>
        <p>To nie diagnoza. Najlepiej umówić pełną diagnozę i wywiad z terapeutą SI - dostaniecie jasny plan wsparcia.</p>
      </>
    );
  }
  if (pct >= 40) {
    return (
      <>
        <p><strong>40-60%</strong> - test nie jest diagnozą, ale warto porozmawiać o mocnych stronach i trudnościach dziecka.</p>
        <p>Na spotkaniu otrzymacie konkretne wskazówki do domu oraz przedszkola/szkoły.</p>
      </>
    );
  }
  return (
    <>
      <p><strong>0-30%</strong> - sygnały mieszczą się w granicach normy.</p>
      <p>Jeśli coś Was niepokoi, zapraszam do kontaktu.</p>
    </>
  );
}

export default function TestSensomotoryczny() {
  const [answers, setAnswers] = useState({});
  const [touched, setTouched] = useState(false);

  const totalAnswered = useMemo(() => Object.keys(answers).length, [answers]);
  const takCount = useMemo(() => Object.values(answers).filter(v => v === 'TAK').length, [answers]);
  const percent = useMemo(() => totalAnswered ? Math.round((takCount / totalAnswered) * 100) : 0, [takCount, totalAnswered]);

  const onChange = (idx, value) => setAnswers(prev => ({ ...prev, [idx]: value }));
  const onSubmit = (e) => { e.preventDefault(); setTouched(true); };
  const onReset = () => { setAnswers({}); setTouched(false); };

  return (
    <section className={styles.wrapper}>
      <SEO
        title="Wstępny test sensomotoryczny (SI) - Sensorini Olsztyn"
        description="Kwestionariusz dla rodziców. Sprawdź wstępnie, czy warto skonsultować dziecko z terapeutą Integracji Sensorycznej w Olsztynie."
        pathname="/test-sensomotoryczny/"
        type="article"
      />

      <div className={styles.card}>
        <header className={styles.header}>
          <h1 className={styles.title}>Wstępny test sensomotoryczny (SI)</h1>
          <p className={styles.lead}>
            Zaznacz <strong>TAK</strong> lub <strong>NIE</strong> przy każdym pytaniu. Wynik to odsetek odpowiedzi „TAK”.
            Ten kwestionariusz <strong>nie jest diagnozą</strong>.
          </p>
        </header>

        <form className={styles.form} onSubmit={onSubmit}>
          <ol className={styles.questions}>
            {QUESTIONS.map((q, idx) => (
              <li className={styles.questionItem} key={idx}>
                <fieldset className={styles.fieldset}>
                  <legend className={styles.questionText}>{q}</legend>
                  <div className={styles.radios}>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`q-${idx}`}
                        value="TAK"
                        checked={answers[idx] === 'TAK'}
                        onChange={() => onChange(idx, 'TAK')}
                      />
                      <span>TAK</span>
                    </label>
                    <label className={styles.radioLabel}>
                      <input
                        type="radio"
                        name={`q-${idx}`}
                        value="NIE"
                        checked={answers[idx] === 'NIE'}
                        onChange={() => onChange(idx, 'NIE')}
                      />
                      <span>NIE</span>
                    </label>
                  </div>
                </fieldset>
              </li>
            ))}
          </ol>

          <div className={styles.actions}>
            <button type="submit" className={styles.primaryBtn}>Policz wynik</button>
            <button type="button" className={styles.ghostBtn} onClick={onReset}>Wyczyść</button>
          </div>
        </form>

        {touched && (
          <section className={styles.resultSection} aria-live="polite">
            <div className={styles.scoreRow}>
              <div className={styles.scoreBadge}>{percent}%</div>
              <div className={styles.progressWrapper}>
                <div className={styles.progressTrack}>
                  <div
                    className={styles.progressBar}
                    style={{ width: `${percent}%` }}
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                  />
                </div>
                <div className={styles.counter}>
                  {takCount} × „TAK” / {totalAnswered || QUESTIONS.length} odpowiedzi
                </div>
              </div>
            </div>

            <div className={styles.feedback}>
              <p className={styles.noteTitle}>Drodzy Rodzice,</p>
              <div className={styles.feedbackText}>{getFeedback(percent)}</div>
            </div>
          </section>
        )}

        {/* --- LISTA + KONTAKT NA DOLE --- */}
        <aside className={styles.callout} role="note">
          <p className={styles.calloutTitle}>Diagnoza to pierwszy krok do rozwiązania problemu:</p>
          <p className={styles.calloutSubtitle}>NIE CZEKAJ, JEŚLI:</p>
          <ul className={styles.calloutList}>
            <li>objawy utrzymują się dłużej niż kilka miesięcy,</li>
            <li>dziecko ma trudności w szkole lub przedszkolu,</li>
            <li>czujesz, że „coś jest nie tak”.</li>
          </ul>

          <div className={styles.contactBox}>
            <p className={styles.contactLead}>Skontaktuj się ze mną - napisz lub zadzwoń:</p>
            <div className={styles.contactLinks}>
              <a className={styles.contactLink} href={`tel:${FIRMA.telefonE164}`}>tel. {FIRMA.telefon}</a>
              <a className={styles.contactLink} href={`mailto:${FIRMA.email}`}>{FIRMA.email}</a>
            </div>
          </div>
        </aside>
      </div>
    </section>
  );
}
